import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Trans, t } from '@lingui/macro';
import { useController, useFormContext } from 'react-hook-form';
import {
  Checkbox,
  DataAmount,
  DataItem,
  FeedbackNotification,
  HeadingM,
  Selector,
  Spinner,
  useToast,
} from '@toqio/toqio-web-components';
import { useBridge } from '../../../bridge/useBridge';
import { useFlowReady } from '../../../bridge/useFlowReady';
import { PrimaryButton, SecondaryButton } from '../../../shared/components';
import { logoUrl } from '../../../shared/assets';
import { createPayment, fetchBootstrap } from '../api';
import type { Bank, ExternalBankValues } from '../schema';
import { decodePispJwt } from '../jwt';
import { identifiersComplete } from '../accountIdentifiers';
import { BankPickerModal } from './BankPickerModal';
import { AccountIdentifierFields } from './AccountIdentifierFields';
import { AuthorizationRequiredModal } from './AuthorizationRequiredModal';

const FLOW_ID = 'external-bank';

// Single provider in this run; telemetry no longer reads it off each bank
// (the new `/banks` contract dropped the per-bank `provider` field).
const PROVIDER = 'VOLT';

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
  max-width: 920px;
  margin: 0 auto;
`;

const Logo = styled.img`
  max-width: 200px;
  max-height: 64px;
  align-self: flex-start;
  object-fit: contain;
`;

const Columns = styled.div`
  display: grid;
  gap: 24px;
  grid-template-columns: 1fr;
  @media (min-width: 768px) {
    grid-template-columns: 1.2fr 1fr;
  }
`;

const Left = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SummaryCard = styled.aside`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
  border-radius: ${({ theme }) =>
    (theme.colors?.cards_default_border_radius as string | undefined) ?? '12px'};
  background: ${({ theme }) =>
    (theme.colors?.cards_default_background_color as string | undefined) ?? '#fff'};
  border: 1px solid
    ${({ theme }) =>
    (theme.colors?.cards_default_border_color as string | undefined) ?? '#e0e0e0'};
`;

const SelectorClickable = styled.div`
  cursor: pointer;
  &:focus-visible {
    outline: 2px solid
      ${({ theme }) =>
    (theme.colors?.input_focus_border_color as string | undefined) ?? '#2684ff'};
    outline-offset: 2px;
    border-radius: 6px;
  }
`;

const CenteredSpinner = styled.div`
  display: flex;
  justify-content: center;
  padding: 64px 0;
`;

const useStringInput = (key: string): string | null => {
  const bridge = useBridge();
  return useMemo(() => {
    const value = (bridge.input as Record<string, unknown>)[key];
    return typeof value === 'string' ? value : null;
  }, [bridge.input, key]);
};

type Phase = 'loading' | 'ready' | 'submitting';

export const Step1Initiate: React.FC = () => {
  const bridge = useBridge();
  const toast = useToast();
  const { control, setValue, watch } = useFormContext<ExternalBankValues>();
  const jwt = useStringInput('jwt');

  const [phase, setPhase] = useState<Phase>('loading');
  const [bootstrapError, setBootstrapError] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);
  // Decoupled push-authorisation (SEB-1714): the create-payment response asked
  // the payer to authorise on their device; we hold the paymentId to build the
  // callback redirect once they tap continue.
  const [authRequired, setAuthRequired] = useState(false);
  const [authPaymentId, setAuthPaymentId] = useState<string | null>(null);

  const bootstrap = watch('bootstrap');
  const selectedBank = watch('selectedBank');
  const termsAccepted = watch('termsAccepted');
  const accountIdentifiers = watch('accountIdentifiers');

  const requiredIdentifiers = selectedBank?.inputRequired ?? [];

  const { field: termsField } = useController<ExternalBankValues, 'termsAccepted'>({
    name: 'termsAccepted',
    control,
  });

  // Emit step:change on mount; we're bypassing StepWizard.
  useEffect(() => {
    bridge.emit({
      type: 'step:change',
      flowId: FLOW_ID,
      stepId: 'initiate',
      stepIndex: 0,
      totalSteps: 1,
    });
    // We only want to fire once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // external-bank is a `manual` readiness flow (see flows/index.ts): we drop
  // the host overlay ourselves once bootstrap resolves and we're about to
  // render real content (form or error) — not the internal Spinner of
  // `phase === 'loading'`. `signalReady` is idempotent.
  const { signalReady } = useFlowReady();
  useEffect(() => {
    if (phase === 'loading') return;
    signalReady();
  }, [phase, signalReady]);

  // Host back signal → cancel the embed.
  useEffect(() => {
    return bridge.onBackRequest(() => {
      bridge.emit({ type: 'cancel', flowId: FLOW_ID });
    });
  }, [bridge]);

  const runBootstrap = useCallback(async () => {
    if (!jwt) {
      setBootstrapError(null);
      setPhase('ready');
      bridge.emit({
        type: 'telemetry',
        event: 'external-bank.bootstrap.error',
        props: { code: 'NO_JWT' },
      });
      return;
    }
    setPhase('loading');
    setBootstrapError(null);
    try {
      const response = await fetchBootstrap(jwt);
      setValue(
        'bootstrap',
        {
          code: response.code,
          description: response.description,
          banks: response.banks,
          termsUrl: response.termsUrl,
        },
        { shouldDirty: false },
      );
      if (response.code !== 'OK') {
        setBootstrapError(response.description ?? null);
        bridge.emit({
          type: 'telemetry',
          event: 'external-bank.bootstrap.error',
          props: {
            code: response.code,
            description: response.description ?? '',
          },
        });
      } else {
        bridge.emit({
          type: 'telemetry',
          event: 'external-bank.bootstrap.success',
          props: {
            provider: PROVIDER,
            banks_count: response.banks.length,
          },
        });
      }
    } catch (e) {
      setBootstrapError(null); // null marks "network" error; UI uses a generic copy.
      bridge.emit({
        type: 'telemetry',
        event: 'external-bank.bootstrap.error',
        props: { code: 'NETWORK', description: e instanceof Error ? e.message : '' },
      });
    } finally {
      setPhase('ready');
    }
  }, [jwt, bridge, setValue]);

  useEffect(() => {
    void runBootstrap();
  }, [runBootstrap]);

  const handleBankSelected = (bank: Bank) => {
    setValue('selectedBank', bank, { shouldDirty: true });
    // Drop the previous bank's inputs so the account-identifier form stays in
    // sync with the picked bank (SEB-1664).
    setValue('accountIdentifiers', {}, { shouldDirty: false });
    setPickerOpen(false);
    bridge.emit({
      type: 'telemetry',
      event: 'external-bank.bank.selected',
      props: { bank_id: bank.id, provider: PROVIDER },
    });
  };

  const handleIdentifierChange = (key: string, value: string) => {
    setValue(
      'accountIdentifiers',
      { ...accountIdentifiers, [key]: value },
      { shouldDirty: true },
    );
  };

  const handleTermsChange = (checked: boolean) => {
    termsField.onChange(checked);
    bridge.emit({
      type: 'telemetry',
      event: 'external-bank.terms.toggled',
      props: { accepted: checked },
    });
  };

  const handleContinue = async () => {
    if (!jwt || !selectedBank || !termsAccepted) return;
    setPhase('submitting');
    bridge.emit({ type: 'telemetry', event: 'external-bank.continue.clicked' });
    try {
      const response = await createPayment(
        jwt,
        selectedBank,
        termsAccepted,
        accountIdentifiers,
      );
      // Happy path: the bank returned an SCA redirect URL.
      if (response.code === 'OK' && response.redirectUrl) {
        bridge.emit({
          type: 'telemetry',
          event: 'external-bank.payment.created',
          props: { provider: PROVIDER },
        });
        bridge.emit({
          type: 'navigate:native',
          route: response.redirectUrl,
          params: { sameApp: true },
        });
        // Stay on submitting state; the host owns the redirect.
        return;
      }
      // Decoupled push-authorisation (SEB-1714): a successful create with no
      // redirect — the payer authorises via a push notification and then taps
      // continue. Bank-agnostic: branch on the presence of authorizationStatus.
      if (response.code === 'OK' && response.authorizationStatus) {
        setAuthPaymentId(response.paymentId ?? null);
        setAuthRequired(true);
        bridge.emit({
          type: 'telemetry',
          event: 'external-bank.payment.authorization-required',
          props: { provider: PROVIDER },
        });
        return;
      }
      // Anything else is an error the payer can retry.
      toast.error(
        'external-bank-payment-error',
        response.description ??
        t`We couldn't create the payment. Please try again.`,
      );
      bridge.emit({
        type: 'telemetry',
        event: 'external-bank.payment.error',
        props: {
          code: response.code,
          description: response.description ?? '',
        },
      });
      setPhase('ready');
    } catch (e) {
      toast.error(
        'external-bank-payment-error',
        t`We couldn't create the payment. Please try again.`,
      );
      bridge.emit({
        type: 'telemetry',
        event: 'external-bank.payment.error',
        props: { code: 'NETWORK', description: e instanceof Error ? e.message : '' },
      });
      setPhase('ready');
    }
  };

  const isBootstrapOk = bootstrap.code === 'OK';
  const identifiersValid = identifiersComplete(
    requiredIdentifiers,
    accountIdentifiers,
    selectedBank && { name: selectedBank.name, country: selectedBank.country },
  );
  const canContinue =
    isBootstrapOk &&
    Boolean(selectedBank) &&
    Boolean(termsAccepted) &&
    identifiersValid &&
    phase === 'ready';

  // Payment params come from the PISP JWT claims (the `/banks` response no
  // longer carries them). Decode for display only — see `jwt.ts`.
  const params = useMemo(() => (jwt ? decodePispJwt(jwt) : null), [jwt]);
  const logoSrc = params?.customerId ? logoUrl(params.customerId) : null;

  // Push-auth CTA: finish the payment by redirecting to the merchant callback
  // with the paymentId, mirroring Step2Callback's navigate-to-callback pattern.
  const handleAuthorizationContinue = () => {
    const callbackUrl = params?.callbackUrl;
    if (!callbackUrl) {
      // No callback claim to return to — surface an error and reopen the form
      // instead of stranding the payer behind the non-dismissable modal.
      setAuthRequired(false);
      setPhase('ready');
      toast.error(
        'external-bank-payment-error',
        t`We couldn't create the payment. Please try again.`,
      );
      bridge.emit({
        type: 'telemetry',
        event: 'external-bank.authorization.error',
        props: { code: 'NO_CALLBACK_URL' },
      });
      return;
    }
    const target = authPaymentId
      ? `${callbackUrl}?paymentId=${encodeURIComponent(authPaymentId)}`
      : callbackUrl;
    bridge.emit({
      type: 'telemetry',
      event: 'external-bank.authorization.continue',
      props: { provider: PROVIDER },
    });
    bridge.emit({ type: 'navigate:native', route: target, params: { sameApp: true } });
  };

  const formattedAmount = useMemo(() => {
    if (!params) return '';
    const locale = bridge.locale ?? 'en';
    const numeric = Number(params.amount);
    if (Number.isNaN(numeric)) return params.amount ?? '';
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numeric);
  }, [params, bridge.locale]);

  if (phase === 'loading') {
    return (
      <Page>
        <CenteredSpinner>
          <Spinner />
        </CenteredSpinner>
      </Page>
    );
  }

  const showBootstrapError = bootstrapError !== null || !isBootstrapOk;
  if (showBootstrapError && !params) {
    const message =
      bootstrapError ?? t`We couldn't load the payment information. Please try again.`;
    return (
      <Page>
        <FeedbackNotification
          variant="error"
          message={message}
          rightActions={
            <SecondaryButton size="s" onClick={runBootstrap}>
              <Trans>Retry</Trans>
            </SecondaryButton>
          }
        />
      </Page>
    );
  }

  return (
    <Page>
      {logoSrc && !logoFailed && params && (
        <Logo
          src={logoSrc}
          alt={params.externalCustomerId}
          onError={() => setLogoFailed(true)}
        />
      )}
      <Columns>
        <Left>
          <SelectorClickable
            role="button"
            tabIndex={0}
            onClick={() => setPickerOpen(true)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setPickerOpen(true);
              }
            }}
          >
            <Selector
              label={t`Where do you want to pay from?`}
              placeholder={t`Select a bank`}
              value={
                selectedBank
                  ? {
                    name: selectedBank.name,
                    avatarImage: selectedBank.logoUrl,
                  }
                  : undefined
              }
            />
          </SelectorClickable>

          {selectedBank && requiredIdentifiers.length > 0 && (
            <AccountIdentifierFields
              keys={requiredIdentifiers}
              bank={{ name: selectedBank.name, country: selectedBank.country }}
              values={accountIdentifiers}
              onChange={handleIdentifierChange}
              disabled={phase === 'submitting'}
            />
          )}

          <Checkbox
            checked={Boolean(termsField.value)}
            onChange={handleTermsChange}
            disabled={!isBootstrapOk}
            label={
              <Trans>
                I confirm that I have read and agree to the{' '}
                <a
                  href={bootstrap.termsUrl || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Terms and Conditions and Privacy Policy
                </a>{' '}
                of the payment initiation provider and its connectivity providers
              </Trans>
            }
          />

          <PrimaryButton disabled={!canContinue} onClick={handleContinue}>
            <Trans>Continue</Trans>
          </PrimaryButton>
        </Left>

        {params && (
          <SummaryCard>
            <HeadingM>
              <Trans>Payment summary</Trans>
            </HeadingM>
            <DataAmount
              label={t`Total to pay`}
              value={formattedAmount}
              currency={params.currency}
              size="m"
              weight="bold"
            />
            <DataItem label={t`To`} value={params.externalCustomerId} />
            {params.customField2 && (
              <DataItem label={t`Description`} value={params.customField2} />
            )}
            <DataItem label={t`Bank reference`} value={params.concept} />
          </SummaryCard>
        )}
      </Columns>

      <BankPickerModal
        open={pickerOpen}
        banks={bootstrap.banks ?? []}
        selectedId={selectedBank?.id}
        onSelect={handleBankSelected}
        onClose={() => setPickerOpen(false)}
      />

      <AuthorizationRequiredModal
        open={authRequired}
        onContinue={handleAuthorizationContinue}
      />
    </Page>
  );
};
